import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Alert, Button, GlassCard } from '@jumpstone/ui-react';
import { triggerDownload } from '../../../lib/download';
import {
  buildMigrationTemplate,
  type MigrationTemplateContext,
} from './migration_template_builder';

/**
 * Step 2 — Template: download the pre-formatted .xlsx or the four CSVs built
 * from the jurisdiction's column spec. JSX-only; the files come from the pure
 * `buildMigrationTemplate`.
 */
export function MigrationTemplateStep({
  templateContext,
  existingEmployeeCount,
  onBack,
  onNext,
}: {
  templateContext: MigrationTemplateContext | null;
  existingEmployeeCount: number;
  onBack: () => void;
  onNext: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [error, setError] = useState(false);

  async function download(kind: 'xlsx' | 'csv'): Promise<void> {
    if (!templateContext) return;
    setError(false);
    try {
      const { xlsx, csvs } = buildMigrationTemplate(templateContext);
      if (kind === 'xlsx') {
        await triggerDownload(
          new Blob([xlsx as BlobPart], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }),
          'wagepro_import_template.xlsx',
        );
      } else {
        for (const csv of csvs) {
          await triggerDownload(
            new Blob([csv.content], { type: 'text/csv;charset=utf-8' }),
            csv.filename,
          );
        }
      }
    } catch (err) {
      console.error('[wagepro-web] migration: template build failed:', err);
      setError(true);
    }
  }

  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-[color:var(--ink-muted)]">
          {t('migration.template.description')}
        </p>

        {existingEmployeeCount > 0 && (
          <Alert variant="info">
            {t('migration.template.existing_warning', {
              count: existingEmployeeCount,
            })}
          </Alert>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            disabled={!templateContext}
            onClick={() => {
              void download('xlsx');
            }}
          >
            {t('migration.button.download_xlsx')}
          </Button>
          <Button
            variant="ghost"
            disabled={!templateContext}
            onClick={() => {
              void download('csv');
            }}
          >
            {t('migration.button.download_csv')}
          </Button>
        </div>

        {error && (
          <Alert variant="danger" title={t('migration.error.generic')} />
        )}

        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            {t('migration.button.back')}
          </Button>
          <Button onClick={onNext}>{t('migration.button.next')}</Button>
        </div>
      </div>
    </GlassCard>
  );
}
